import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { TargetingIcon } from "orion-components/SharedComponents";
import PropTypes from "prop-types";
import MainMenu from "./MainMenu/MainMenu";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate } from "orion-components/i18n";
import { SvgIcon, Tooltip } from "@mui/material";
import { mdiFullscreen, mdiFullscreenExit } from "@mdi/js";
import { useMemo } from "react";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";
import { makeSelectComponentProp } from "../../../SharedComponents/ComponentState/redux/selectors";

// Constants
const VIDEO_MODES = {
	LIVE: "Live",
	RECORDED: "Recorded"
};

const COLORS = {
	WHITE: "#FFFFFF",
	LIVE_INDICATOR: "#21C230",
	RECORDED_INDICATOR: "#BB3C1E",
	BACKGROUND_OVERLAY: "rgba(0, 0, 0, 0.5)",
	TRANSPARENT: "transparent"
};

const DIMENSIONS = {
	TOP_BAR_HEIGHT: 48,
	ICON_SIZE: 18,
	FULLSCREEN_ICON_SIZE: 24,
	TOOLTIP_OFFSET: -30,
	Z_INDEX_BASE: 1904,
	Z_INDEX_WRAPPER: 1904,
	Z_INDEX_LIVE_INDICATOR: 1905
};

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const VideoTopBar = ({
	camera,
	getSelectedVideoMode,
	isInFullScreen,
	expanded,
	inDock,
	isInVideoWall,
	removeFromWall,
	toggleFullScreenCameraDialog,
	currentVideoMode,
	clusterProps,
	isOnAlertProfile,
	hideRemoveFromWall,
	videoPlayerHovered = false,
	instanceId
}) => {
	const { entityData } = camera;
	const cameraName = entityData.properties.name;
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const dir = useSelector(state => getDir(state));
	const [switchVideoMode, setSwitchVideoMode] = useState(VIDEO_MODES.LIVE);
	const videoPlayerAlwaysShowName = useSelector(
		state => getEcoSettingByKey("videoPlayerAlwaysShowName")(state) || false
	);

	const isJumpToDifferentTimeOpen = useSelector(
		makeSelectComponentProp(COMPONENT_KEY, instanceId, "components.recorded.jumpToDifferentTime.isOpen")
	);

	// Derived state
	const shouldShowTopBarContent = (videoPlayerAlwaysShowName || videoPlayerHovered) && !isJumpToDifferentTimeOpen;
	const shouldShowMenuAndTargeting = videoPlayerHovered && !isJumpToDifferentTimeOpen;
	const isLiveMode = switchVideoMode === VIDEO_MODES.LIVE;

	const getMenuState = value => {
		setIsMenuOpen(value);
	};

	const handleVideoModeToggle = () => {
		const newMode = isLiveMode ? VIDEO_MODES.RECORDED : VIDEO_MODES.LIVE;
		setSwitchVideoMode(newMode);
		getSelectedVideoMode(newMode);
	};

	const handleFullScreenToggle = () => {
		toggleFullScreenCameraDialog(!isInFullScreen);
	};

	useEffect(() => {
		if (currentVideoMode !== "" && currentVideoMode !== switchVideoMode) {
			setSwitchVideoMode(currentVideoMode);
		}
	}, [currentVideoMode, switchVideoMode]);

	const styles = useMemo(() => {
		const isRtl = dir === "rtl";

		return {
			// ...existing styles object...
			container: {
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				zIndex: DIMENSIONS.Z_INDEX_BASE
			},
			topBarWrapper: {
				zIndex: DIMENSIONS.Z_INDEX_WRAPPER,
				display: "flex",
				flexDirection: "row",
				fontSize: 12,
				width: "100%",
				height: DIMENSIONS.TOP_BAR_HEIGHT,
				backgroundColor: shouldShowTopBarContent ? COLORS.BACKGROUND_OVERLAY : COLORS.TRANSPARENT,
				alignItems: "center",
				justifyContent: "end"
			},
			menuAndTargetingContainer: {
				display: shouldShowMenuAndTargeting ? "flex" : "none",
				flexDirection: "row"
			},
			cameraNameContainer: {
				display: shouldShowTopBarContent ? "flex" : "none",
				alignItems: "center",
				justifyContent: "center",
				flex: 1,
				overflow: "hidden",
				textOverflow: "ellipsis",
				margin: "0 8px"
			},
			cameraNameText: {
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				width: "auto",
				fontSize: "1em",
				textTransform: "uppercase",
				color: COLORS.WHITE
			},
			modeAndFullScreenContainer: {
				display: "flex",
				flexDirection: "row",
				alignItems: "center"
			},
			videoModeText: {
				fontSize: "1em",
				textTransform: "uppercase",
				color: COLORS.WHITE,
				cursor: "pointer",
				display: videoPlayerHovered ? "block" : "none",
				...(isRtl ? { marginLeft: 8 } : { marginRight: 8 })
			},
			liveIndicator: {
				backgroundColor: isLiveMode ? COLORS.LIVE_INDICATOR : COLORS.RECORDED_INDICATOR,
				...(isRtl ? { marginLeft: "4px" } : { marginRight: "4px" })
			},
			fullScreenButton: {
				display: "flex",
				alignItems: "center"
			},
			fullScreenIcon: {
				width: `${DIMENSIONS.FULLSCREEN_ICON_SIZE}px`,
				height: `${DIMENSIONS.FULLSCREEN_ICON_SIZE}px`,
				color: COLORS.WHITE,
				cursor: "pointer"
			},
			targetingIconContainer: {
				width: DIMENSIONS.ICON_SIZE,
				height: DIMENSIONS.ICON_SIZE
			}
		};
	}, [dir, shouldShowTopBarContent, shouldShowMenuAndTargeting, videoPlayerHovered, isLiveMode]);

	const tooltipProps = {
		title: cameraName,
		placement: "bottom",
		slotProps: {
			popper: {
				modifiers: [
					{
						name: "offset",
						options: {
							offset: [0, DIMENSIONS.TOOLTIP_OFFSET]
						}
					}
				]
			}
		}
	};

	const targetingIconProps = {
		geometry: entityData.geometry,
		id: camera.id,
		feedId: camera.feedId,
		customStyles: styles.targetingIconContainer,
		iconContainerStyle: { padding: 0 }
	};

	return (
		<div dir={dir} style={styles.container}>
			<div className="bccVideoTopBarWrapper" style={styles.topBarWrapper}>
				{/* Menu and Targeting Section */}
				<div id="menuAndTargetingIcon" style={styles.menuAndTargetingContainer}>
					<MainMenu
						getMenuState={getMenuState}
						camera={camera}
						inDock={inDock}
						isInVideoWall={isInVideoWall}
						removeFromWall={removeFromWall}
						clusterProps={clusterProps}
						isOnAlertProfile={isOnAlertProfile}
						hideRemoveFromWall={hideRemoveFromWall}
						instanceId={instanceId}
					/>

					{!isMenuOpen && !isInFullScreen && <TargetingIcon {...targetingIconProps} />}
				</div>

				{/* Camera Name Section */}
				<div id="cameraName" style={styles.cameraNameContainer}>
					<Tooltip {...tooltipProps}>
						<div style={styles.cameraNameText}>{cameraName}</div>
					</Tooltip>
				</div>

				{/* Mode and Full Screen Section */}
				<div id="modeAndFullScreen" style={styles.modeAndFullScreenContainer}>
					<div style={styles.videoModeText} onClick={handleVideoModeToggle}>
						{isLiveMode ? (
							<Translate value="global.CBComponents.newVideoPlayer.components.videoTopBar.live" />
						) : (
							<Translate value="global.CBComponents.newVideoPlayer.components.videoTopBar.recorded" />
						)}
					</div>

					<div className="bcc_liveIndicator" style={styles.liveIndicator} />

					<div style={styles.fullScreenButton}>
						<SvgIcon onClick={handleFullScreenToggle} style={styles.fullScreenIcon}>
							<path d={!isInFullScreen ? mdiFullscreen : mdiFullscreenExit} />
						</SvgIcon>
					</div>
				</div>
			</div>
		</div>
	);
};

VideoTopBar.propTypes = {
	camera: PropTypes.shape({
		entityData: PropTypes.shape({
			properties: PropTypes.shape({
				name: PropTypes.string.isRequired
			}).isRequired,
			geometry: PropTypes.object.isRequired
		}).isRequired,
		id: PropTypes.string.isRequired,
		feedId: PropTypes.string.isRequired
	}).isRequired,
	getSelectedVideoMode: PropTypes.func.isRequired,
	isInFullScreen: PropTypes.bool,
	expanded: PropTypes.bool,
	inDock: PropTypes.bool,
	isInVideoWall: PropTypes.bool,
	removeFromWall: PropTypes.func,
	toggleFullScreenCameraDialog: PropTypes.func.isRequired,
	currentVideoMode: PropTypes.string,
	clusterProps: PropTypes.object,
	isOnAlertProfile: PropTypes.bool,
	hideRemoveFromWall: PropTypes.bool,
	videoPlayerHovered: PropTypes.bool
};

VideoTopBar.defaultProps = {
	isInFullScreen: false,
	expanded: false,
	inDock: false,
	isInVideoWall: false,
	currentVideoMode: "",
	isOnAlertProfile: false,
	hideRemoveFromWall: false,
	videoPlayerHovered: false,
	removeFromWall: () => {},
	clusterProps: null
};

export default VideoTopBar;
